import 'react-datetime/css/react-datetime.css'
import * as React from 'react'
import { Route, Switch } from 'react-router-dom'
import smartlookClient from 'smartlook-client'
import ErrorBar from '../containers/ErrorBar'
import ProviderProductPane from '../containers/products/ProviderProductPane'
import RidersPane from '../containers/riders/RidersPane'
import ServicePane from '../containers/services/ServicePane'
import TripsHistoryPane from '../containers/trips/TripsHistory'
import { getAccessJwtPayload, initTokenRefresh, isTokenValid } from '../utils/auth'
import AssistancePane from './assistance/AssistancePane'
import AuthenticationPane from './authentication/AuthenticationPane'
import MembershipPane from './membership-request/MembershipPane'
import MobilityOptionsPane from './mobility-options/MobilityOptionsPane'
import AuthRouteWithMenu from './navigation/AuthRouteWithMenu'
import AuthRoute from './navigation/AuthRoute'
import OrganizationsPane from './organizations/OrganizationsPane'
import TeamMembersPane from './team-members/TeamMemberPane'
import StationUserPane from './station-user/StationUserPane'
import DriversPane from '../containers/drivers/DriversPane'
import VehiclesPane from './vehicles/VehiclesPane'
import { IAuthJwtPayloadDTO } from '../types/user'
import { IUserAuthorizationState } from '../types'
import TripsPane from '../containers/trips/TripsPane'
import GlobalSettingsPane from '../containers/global-settings/GlobalSettingsPane'
import TripsManagementPane from '../containers/trips/TripsManagementPane'
import PromotionsPane from '../containers/promotions/PromotionsPane'
import TripFeedbackPane from '../containers/trip-feedbacks/TripFeedbackPane'
import DriverLandingPage from '../components/drivers/LandingPage'
import RiderLandingPage from '../components/riders/LandingPage'
import { IOrgObj } from 'src/types'
import StationUserTripsPane from '../containers/station-user-trips/StationUserTripsPane'
import ImportTripPane from '../containers/import/Import'
import TripsScheduler from '../containers/trips/TripsScheduler'
import BillingManagementPane from 'src/containers/trips/BillingManagementPane'
import SuccessBar from 'src/containers/SuccessBar'
import { setOptions } from '@mobiscroll/react'
import DriverManifestsPage from './driver-manifests/DriverManifestsPage'

setOptions({
  display: 'anchored',
  inputComponent: 'input',
  showArrow: true,
  showOverlay: false,
  theme: 'windows',
  touchUi: true
})

const NoMatch = ({ location }: { location: any }) => (
  <div>
    <h3>
      <code>{location.pathname}</code> not found.
    </h3>
  </div>
)

interface IAppProps {
  history: any
  location: any
  currentOrgId: string
  auth: IUserAuthorizationState
  organization: IOrgObj
  setCurrentOrg: (organizationId: string) => void
  getUserInfo: (id: string) => void
  setCurrentUserToStore: (accessJwtPayload: IAuthJwtPayloadDTO) => void
}

class App extends React.Component<IAppProps> {
  private currentuserId: string | number
  private currentOrganizationId: string

  constructor(props: IAppProps) {
    super(props)
    initTokenRefresh()
    if (process.env.REACT_APP_ENV === 'production') {
      smartlookClient.init('75a7d4b4ea896187c6d7bbf4aec8e1ca47c48e2e')
    }
  }

  public componentDidMount() {
    this.setCurrentToStore()
  }

  public componentDidUpdate() {
    this.setCurrentToStore()
  }

  public identifySmartlookUser = ({ id, firstName, lastName, email }: any) => {
    if (process.env.REACT_APP_ENV === 'production') {
      smartlookClient.identify(id, {
        name: `${firstName} ${lastName}`,
        email
      })
    }
  }

  public setCurrentToStore() {
    // TODO: Remove this method when select current organization functionality is implemented
    if (isTokenValid()) {
      const accessJwtPayload = getAccessJwtPayload()
      if (accessJwtPayload && this.currentuserId !== accessJwtPayload.id) {
        this.props.setCurrentUserToStore(accessJwtPayload)
        this.identifySmartlookUser(accessJwtPayload)
        this.currentuserId = accessJwtPayload.id
      }
      if (
        accessJwtPayload &&
        accessJwtPayload.rolesInOrganizations.length &&
        this.currentOrganizationId !== accessJwtPayload.rolesInOrganizations[0].organizationId
      ) {
        this.props.setCurrentOrg(accessJwtPayload.rolesInOrganizations[0].organizationId)
        this.currentOrganizationId = accessJwtPayload.rolesInOrganizations[0].organizationId
      }

      if (this.props.auth.type === 'driver') {
        if (this.props.location.pathname !== '/driver-landing') {
          this.props.history.push('/driver-landing')
        }
        return
      }

      if (this.props.auth.type === 'rider') {
        if (this.props.location.pathname !== '/rider-landing') {
          this.props.history.push('/rider-landing')
        }
        return
      }

      if (this.props.auth.type === 'station-user') {
        if (!this.props.location.pathname.startsWith('/station-user-trips')) {
          this.props.history.push('/station-user-trips')
        }
        if (this.props.currentOrgId && !this.props.organization) {
          this.props.setCurrentOrg(this.props.currentOrgId)
        }
        return
      }

      // When no organization exists for logged in user redirect to organization creat form
      if (
        accessJwtPayload &&
        accessJwtPayload.rolesInOrganizations.length === 0 &&
        this.props.location.pathname !== '/organizations/create/organization-info'
      ) {
        this.props.history.push('/organizations/create/organization-info')
      }
      const { organization } = this.props
      //maybe we can remove this if for now since all new orgs created moving forward will have org subscription already
      // this code block ensures that all organiztions have a subscription
      // if (
      //   organization &&
      //   !organization.unlimited &&
      //   !organization.subscription &&
      //   this.props.location.pathname !== `/organizations/${organization.id}/edit/organization-subscription`
      // ) {
      //   // this.props.history.push(`/organizations/${organization.id}/edit/organization-subscription`)
      // }
    }
  }

  public render() {
    return (
      <div className='App clearfix'>
        <div id='modal-container' />
        <ErrorBar />
        <SuccessBar />
        <Switch>
          <Route path='/auth' component={AuthenticationPane} />
          <Route path='/driver-landing' component={DriverLandingPage} />
          <Route path='/rider-landing' component={RiderLandingPage} />
          <AuthRouteWithMenu path='/trips-history' component={TripsHistoryPane} />
          <AuthRouteWithMenu path='/trips' component={TripsPane} />
          <AuthRouteWithMenu path='/station-user-trips' component={StationUserTripsPane} />
          <AuthRouteWithMenu path='/trips-management' component={TripsManagementPane} />
          <AuthRouteWithMenu path='/billing-management' component={BillingManagementPane} />
          <AuthRouteWithMenu path='/' exact={true} component={TripsPane} />
          <AuthRouteWithMenu path='/products' component={ProviderProductPane} />
          <AuthRouteWithMenu path='/team-members' component={TeamMembersPane} />
          <AuthRouteWithMenu path='/station-user' component={StationUserPane} />
          <AuthRouteWithMenu path='/riders' component={RidersPane} />
          <AuthRouteWithMenu path='/membership-requests' component={MembershipPane} />
          <AuthRouteWithMenu path='/mobility-options' component={MobilityOptionsPane} />
          <AuthRouteWithMenu path='/assistance' component={AssistancePane} />
          <AuthRouteWithMenu path='/services' component={ServicePane} />
          <AuthRouteWithMenu path='/organizations' component={OrganizationsPane} />
          <AuthRouteWithMenu path='/global-settings' component={GlobalSettingsPane} />
          <AuthRouteWithMenu path='/drivers' component={DriversPane} />
          <AuthRouteWithMenu path='/vehicles' component={VehiclesPane} />
          <AuthRouteWithMenu path='/promotions' component={PromotionsPane} />
          <AuthRouteWithMenu path='/trip-feedbacks' component={TripFeedbackPane} />
          <AuthRouteWithMenu path='/import' component={ImportTripPane} />
          <AuthRouteWithMenu path='/scheduler' component={TripsScheduler} />
          <AuthRouteWithMenu path='/driver-manifests' component={DriverManifestsPage} />
          <Route component={NoMatch} />
        </Switch>
      </div>
    )
  }
}

export default App



// WEBPACK FOOTER //
// ./src/components/App.tsx